import { type RequestPolicy } from 'urql';

import { useIsFocused } from '@sg/garnish';

import { useMenuContentRestaurantQuery } from '../../graphql/MenuContentRestaurant.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContentData = (params: UseMenuContentDataParams) => {
  const { restaurantSlug, requestPolicy = 'cache-and-network' } = params;

  const isScreenFocused = useIsFocused();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [restaurantQueryResponse] = useMenuContentRestaurantQuery({
    variables: { id: restaurantSlug! },
    requestPolicy,
    pause: !isScreenFocused || !restaurantSlug,
  });

  const { fetching, error, data } = restaurantQueryResponse;

  const restaurantData = data?.restaurant;
  const categories = restaurantData?.menu.categories ?? [];

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasNoProducts = fetching
    ? false
    : categories.every((category) => category.products.length === 0);

  // ─────────────────────────────────────────────────────────────────────

  return {
    // NOTE: We capture only the initial fetching to reduce the number of loading states and rerenders.
    isFetching: !data && fetching,
    hasError: error !== undefined,
    hasNoProducts,
    restaurantData,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMenuContentDataParams = {
  restaurantSlug: string | undefined;
  requestPolicy?: RequestPolicy;
};
